import { NotificationType } from '../../api/admin'
import {
    useCreatePipelineJobs,
    useTriggerEmailNotification,
    PipelineStepName,
    RunMode,
    TriggerEmailNotificationDto,
} from '../../hooks/admin/useAdminMutations'
import { AdminUserDto } from '../../types/admin-types'
import { Modal, Tooltip } from 'flowbite-react'
import React, { useState, useEffect } from 'react'
import { HiInformationCircle, HiExternalLink } from 'react-icons/hi'

interface UserActionsModalProps {
    show: boolean
    onClose: () => void
    user: AdminUserDto | null
    isAllUsers: boolean
    users: AdminUserDto[]
}

const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never'
    try {
        return new Date(dateString).toLocaleString()
    } catch (e) {
        return 'Invalid Date'
    }
}

const UserActionsModal: React.FC<UserActionsModalProps> = ({
    show,
    onClose,
    user,
    isAllUsers,
    users,
}) => {
    const [runMode, setRunMode] = useState<RunMode>(RunMode.INDIVIDUAL)
    const [transcriptChapterId, setTranscriptChapterId] = useState<string>('')
    const [dryRun, setDryRun] = useState<boolean>(true)
    const [totalPendingChapters, setTotalPendingChapters] = useState<number>(0)

    const { mutate: createJobs, isPending: isCreatingJobs } = useCreatePipelineJobs()
    const {
        mutate: triggerEmail,
        isPending: isSendingEmail,
        isSuccess: emailSuccess,
        isError: emailError,
        error: emailErrorData,
        reset: resetEmailMutation,
    } = useTriggerEmailNotification()

    const calculateTotalPendingChapters = () => {
        if (!users || !users.length) return 0
        return users.reduce((total, u) => total + (u.pending_transcript_chapter_count || 0), 0)
    }

    const handleViewContentAsUser = () => {
        if (user && user.username && user.slug) {
            window.open(`/v2/content/${user.username}/${user.slug}`, '_blank')
        } else {
            alert('Missing username or slug to view user content.') // Should not happen if data is correct
        }
    }

    useEffect(() => {
        if (show) {
            if (isAllUsers) {
                setRunMode(RunMode.ALL)
                setTotalPendingChapters(calculateTotalPendingChapters())
            } else {
                setRunMode(RunMode.INDIVIDUAL)
                setTotalPendingChapters(user?.pending_transcript_chapter_count || 0)
            }
            setTranscriptChapterId('')
            setDryRun(true)
            resetEmailMutation()
        } else {
            resetEmailMutation()
        }
    }, [show, isAllUsers, user, users, resetEmailMutation])

    const handleCreateJobs = () => {
        if (!isAllUsers && runMode === RunMode.INDIVIDUAL && !transcriptChapterId) {
            alert('Please enter a Transcript Chapter ID for Individual run mode.')
            return
        }

        let user_ids: string[] | undefined
        const run_mode = isAllUsers ? RunMode.ALL : runMode

        if (isAllUsers) {
            user_ids = users.map((u) => u.id)
        } else if (user) {
            user_ids = [user.id]
        } else {
            alert('No user selected.')
            return
        }

        if (!user_ids || user_ids.length === 0) {
            alert('No users match the selection.')
            return
        }

        const context: any = { run_mode }
        if (!isAllUsers && runMode === RunMode.INDIVIDUAL && transcriptChapterId) {
            context.transcript_chapter_id = transcriptChapterId
        }

        createJobs(
            {
                user_ids,
                step: PipelineStepName.CREATE_DRAFT_FROM_TRANSCRIPT_CHAPTER,
                context: context,
            },
            {
                onSuccess: (data) => {
                    console.log('Pipeline jobs created:', data)
                    alert(`Successfully created ${data.jobs.length} pipeline jobs.`)
                    onClose()
                },
                onError: (error) => {
                    console.error('Error creating pipeline jobs:', error)
                    alert(
                        `Error creating jobs: ${
                            error instanceof Error ? error.message : 'Unknown error'
                        }`,
                    )
                },
            },
        )
    }

    const handleTriggerEmail = () => {
        let user_ids: string[] | undefined
        if (isAllUsers) {
            user_ids = users.map((u) => u.id)
        } else if (user) {
            user_ids = [user.id]
        } else {
            alert('No user selected.')
            return
        }

        if (!user_ids || user_ids.length === 0) {
            alert('No users match the selection.')
            return
        }

        const emailConfig: TriggerEmailNotificationDto = {
            notification_type: NotificationType.NEW_POSTS_AVAILABLE,
            user_ids: user_ids,
            dry_run: dryRun,
        }

        triggerEmail(emailConfig, {
            onSuccess: (data) => {
                alert(
                    `${dryRun ? 'Dry run: Would have processed' : 'Processed'} ${
                        data.usersProcessed
                    } users, ${dryRun ? 'would have sent' : 'sent'} ${data.emailsSent} emails.`,
                )
            },
            onError: (error) => {
                alert(
                    `Error triggering email: ${
                        error instanceof Error ? error.message : 'Unknown error'
                    }`,
                )
            },
        })
    }

    if (!show) {
        return null
    }

    const title = isAllUsers ? 'Actions for All Users' : `Actions for ${user?.name || 'User'}`

    return (
        <Modal show={show} size="lg" onClose={onClose} className="z-[1000]">
            <Modal.Header>
                {title}{' '}
                {!isAllUsers && user && user.username && user.slug && (
                    <button
                        onClick={handleViewContentAsUser}
                        className="ml-4 px-3 py-1 bg-teal-500 text-white rounded text-xs hover:bg-teal-600 inline-flex items-center disabled:opacity-50"
                        title="View user's generated content page"
                    >
                        <HiExternalLink className="mr-1 h-3 w-3" />
                        View Content
                    </button>
                )}
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-6">
                    <fieldset className="border p-4 rounded-md">
                        <legend className="text-lg font-medium px-2">Create Draft Posts</legend>
                        <div className="space-y-4 mt-2">
                            <div className="bg-gray-100 p-3 rounded-md">
                                <h4 className="text-md font-semibold mb-1">
                                    {isAllUsers ? 'Total Pending' : 'Pending'} Transcript Chapters
                                </h4>
                                <p className="text-lg font-bold text-blue-600">
                                    {totalPendingChapters}
                                </p>
                            </div>

                            {!isAllUsers && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Run Mode
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                        value={runMode}
                                        onChange={(e) => setRunMode(e.target.value as RunMode)}
                                        disabled={isCreatingJobs}
                                    >
                                        <option value={RunMode.INDIVIDUAL}>
                                            Individual Chapter
                                        </option>
                                        <option value={RunMode.ALL}>All Chapters</option>
                                    </select>
                                </div>
                            )}

                            {!isAllUsers && runMode === RunMode.INDIVIDUAL && (
                                <div>
                                    <label
                                        htmlFor="transcriptChapterIdInput"
                                        className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        Transcript Chapter ID (Required for Individual Mode)
                                    </label>
                                    <input
                                        id="transcriptChapterIdInput"
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                        value={transcriptChapterId}
                                        onChange={(e) => setTranscriptChapterId(e.target.value)}
                                        placeholder="Enter chapter ID"
                                        required
                                        disabled={isCreatingJobs}
                                    />
                                </div>
                            )}

                            <button
                                onClick={handleCreateJobs}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={
                                    isCreatingJobs ||
                                    (!isAllUsers &&
                                        runMode === RunMode.INDIVIDUAL &&
                                        !transcriptChapterId)
                                }
                            >
                                {isCreatingJobs ? 'Creating Jobs...' : 'Run Selected Step'}
                            </button>
                        </div>
                    </fieldset>

                    <fieldset className="border p-4 rounded-md">
                        <legend className="text-lg font-medium px-2">Email Notification</legend>
                        <div className="space-y-4 mt-2">
                            <p className="text-sm text-gray-600">
                                Trigger 'New Posts Available' email notification for selected
                                user(s).
                            </p>

                            {!isAllUsers && user && (
                                <div className="bg-yellow-50 p-3 text-gray-800 rounded-md text-sm space-y-1">
                                    <p>
                                        New Posts Since Last Email:{' '}
                                        <span className="font-medium">
                                            {user.num_draft_posts_since_last_email ?? 0}
                                        </span>
                                    </p>
                                    <p>
                                        Last Emailed At:{' '}
                                        <span className="font-medium">
                                            {formatDate(user.last_emailed_at)}
                                        </span>
                                    </p>
                                </div>
                            )}
                            {isAllUsers && (
                                <div className="bg-blue-50 p-3 rounded-md">
                                    <p>
                                        Users with New Posts:{' '}
                                        <span className="font-medium">
                                            {
                                                users.filter(
                                                    (u) =>
                                                        (u.num_draft_posts_since_last_email || 0) >
                                                        0,
                                                ).length
                                            }
                                        </span>
                                    </p>
                                </div>
                            )}

                            <div className="flex items-center">
                                <input
                                    id="dryRun"
                                    type="checkbox"
                                    checked={dryRun}
                                    onChange={(e) => setDryRun(e.target.checked)}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    disabled={isSendingEmail}
                                />
                                <label
                                    htmlFor="dryRun"
                                    className="ml-2 block text-sm text-gray-900"
                                >
                                    Dry Run (Simulate only, no emails sent)
                                </label>
                                <div className="ml-2">
                                    <Tooltip content="Check logs for simulation results. If unchecked, emails will be sent based on criteria.">
                                        <HiInformationCircle className="text-gray-500 cursor-help" />
                                    </Tooltip>
                                </div>
                            </div>
                            <button
                                onClick={handleTriggerEmail}
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isSendingEmail}
                            >
                                {isSendingEmail
                                    ? dryRun
                                        ? 'Simulating...'
                                        : 'Sending...'
                                    : dryRun
                                    ? 'Trigger Dry Run'
                                    : 'Trigger Email'}
                            </button>
                            {emailSuccess && (
                                <p className="text-sm text-green-600 text-center">
                                    Notification process completed.
                                </p>
                            )}
                            {emailError && (
                                <p className="text-sm text-red-600 text-center">
                                    Error:{' '}
                                    {emailErrorData instanceof Error
                                        ? emailErrorData.message
                                        : 'Unknown error'}
                                </p>
                            )}
                        </div>
                    </fieldset>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default UserActionsModal
